// Uzaktan destek paneli — bir temsilcinin eklentisine tek bir komut gönderir.
//
// NE DEĞİLDİR: uzaktan kod çalıştırma. Buradaki her komut, temsilcinin yüklü eklenti
// sürümünde derlenmiş SABİT bir 'case'tir; listede olmayan bir şey gönderilemez ve
// eklentinin bilmediği bir komut 'unsupported' olarak geri döner. Yeni bir komut eklemek
// Chrome Web Store'a yeni sürüm yüklemeyi gerektirir — bilerek böyle.
//
// Teslimat, eklentinin ZATEN yaptığı mola-durumu yoklamasına biner: çalışırken ~60 sn,
// molada ~30 sn. Bu yüzden panel "gönderildi" ile "çalıştı"yı asla karıştırmaz —
// satırın durumu (pending → delivered → accepted → done) tek gerçek kaynaktır.

import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelAgentCommand, getAgentCommands, issueAgentCommand } from '../api';
import type { AgentCommand, CommandTier } from '../types';
import { Icon, Modal, inputStyle, primaryBtnStyle, ghostBtnStyle } from '../ui';

interface CommandDef {
  kind: string;
  tier: CommandTier;
  label: string;
  hint: string;
  /** Serbest metin argümanı isteyen komutlar (mesaj metni / sayfa adı / WAL kimlikleri). `ids`
   *  virgül veya boşlukla ayrılır ve bir diziye çevrilir (drop_wal). */
  argKey?: 'text' | 'sheet' | 'ids';
  argLabel?: string;
}

// Sıra = süpervizörün kullanma sırası: önce zararsız teşhis, sonra onarım, en sonda yıkıcı.
const COMMANDS: CommandDef[] = [
  { kind: 'ping', tier: 1, label: 'Bağlantı testi', hint: 'Panel ulaşılabilir mi? Hiçbir şeyi değiştirmez.' },
  { kind: 'diagnostics', tier: 1, label: 'Durum raporu al', hint: 'Salt okunur: sıra, mola, kuyruk ve oturum durumu. Müşteri bilgisi içermez.' },
  { kind: 'show_message', tier: 1, label: 'Mesaj gönder', hint: 'Temsilcinin panelinde bir not gösterir.', argKey: 'text', argLabel: 'Mesaj' },
  { kind: 'set_sheet', tier: 1, label: 'Çalışma sayfasını değiştir', hint: 'Yanlış sekmede çalışan temsilciyi doğru sayfaya alır.', argKey: 'sheet', argLabel: 'Sayfa adı' },
  { kind: 'reset_queue_cursor', tier: 1, label: 'Sıra imlecini sıfırla', hint: 'Aynı müşteride takılmayı / atlanan satırları düzeltir.' },
  { kind: 'clear_rejected_rows', tier: 1, label: 'Atlanan satırları temizle', hint: 'Sunucu düzeldiyse, bu vardiyada reddedilen satırları tekrar denemeye açar. İmleci/sırayı sıfırlamaz.' },
  { kind: 'reset_break', tier: 1, label: 'Mola durumunu sıfırla', hint: '"Molayı bitiremiyorum" durumunu çözer.' },
  { kind: 'flush_wal', tier: 1, label: 'Bekleyen sonuçları gönder', hint: '"Sonuç kuyruğa alındı" mesajında takılı kalanları iter.' },
  { kind: 'clear_cache', tier: 1, label: 'Panel önbelleğini temizle', hint: 'Mola durumuna ve bekleyen sonuçlara DOKUNMAZ.' },
  { kind: 'clear_sms_cache', tier: 1, label: 'SMS şablonlarını yenile', hint: 'Eski/yanlış SMS şablonu (ör. güncellenmiş numara yerine "test") gönderen paneli düzeltir — önbelleği temizler, proxy\'den taze çeker.' },
  { kind: 'refresh_stats', tier: 1, label: 'İstatistikleri yenile', hint: 'Sayaçları sayfadan yeniden çeker (ör. satırı elle düzelttikten sonra).' },
  { kind: 'recover_panel', tier: 2, label: 'Paneli onar (yumuşak)', hint: 'Önbellekleri temizler ve paneli aynı sayfada yeniden başlatır — sayfa YENİLENMEZ, oturum ve bekleyen sonuçlar korunur. Temsilci sonuç giriyorsa reddedilir.' },
  { kind: 'tail_logs', tier: 2, label: 'Panel günlüklerini al', hint: 'Salt okunur: son teknik günlük satırları (arıza tespiti). Müşteri bilgisi içermez.' },
  { kind: 'reload', tier: 2, label: 'Paneli yeniden yükle', hint: 'Sayfayı yeniler. Temsilci sonuç giriyorsa reddedilir.' },
  { kind: 'recapture_session', tier: 2, label: 'Oturumu yeniden al', hint: 'BackOffice anahtarını yeniden yakalar (bağlantı sorunları).' },
  { kind: 'reset_agent', tier: 2, label: 'Paneli tamamen sıfırla', hint: 'İsim sorusu yeniden sorulur. Bekleyen sonuçlara dokunmaz.' },
  { kind: 'export_wal', tier: 3, label: 'Bekleyen sonuçları listele', hint: 'Salt okunur döküm — sonra "düşür" ile tek tek çıkarabilirsiniz.' },
  { kind: 'drop_wal', tier: 3, label: 'Bekleyen sonuçları düşür', hint: 'Takılan sonuçları karantinaya alır (önce "listele" ile kimlikleri alın). Yıkıcı.', argKey: 'ids', argLabel: 'Kimlikler (virgülle ayırın)' },
];

const TIER_NOTE: Record<CommandTier, string> = {
  1: 'Güvenli',
  2: 'Paneli yeniden başlatır',
  3: 'Bekleyen sonuçlara dokunur',
};

const STATUS_TR: Record<string, string> = {
  pending: 'Sırada — tarayıcısına ulaşması bekleniyor',
  delivered: 'Tarayıcısına ulaştı',
  accepted: 'Çalıştırılıyor',
  done: 'Tamamlandı',
  failed: 'Başarısız',
  refused: 'Reddedildi',
  unsupported: 'Eklenti sürümü desteklemiyor',
  expired: 'Süresi doldu — panel açık değildi',
};

const STATUS_COLOR: Record<string, string> = {
  done: '#37c46b',
  failed: '#ef6d6d',
  refused: '#e0a23c',
  unsupported: '#e0a23c',
  expired: 'var(--dim)',
};

const isLive = (c: AgentCommand) => !c.done_at;

export default function AgentCommandModal({
  agentId,
  agentName,
  panelDark = false,
  lastBeatAt = null,
  onClose,
}: {
  agentId: string;
  agentName: string;
  panelDark?: boolean;
  lastBeatAt?: string | null;
  onClose: () => void;
}) {
  const [commands, setCommands] = useState<AgentCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CommandDef>(COMMANDS[0]);
  const [argValue, setArgValue] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await getAgentCommands(agentId);
      if (alive.current) setCommands(r.commands);
    } catch (e) {
      if (alive.current) setErr(e instanceof Error ? e.message : 'Komutlar okunamadı');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    alive.current = true;
    void load();
    // Teslimat yoklamaya bindiği için sonuç 30-60 sn sonra gelir; panel bunu canlı göstermeli,
    // yoksa süpervizör "çalıştı mı?" diye tahmin eder.
    const t = window.setInterval(() => void load(), 5000);
    return () => { alive.current = false; window.clearInterval(t); };
  }, [load]);

  const live = commands.find(isLive) || null;
  // 2. ve 3. kademe komutlar paneli yeniden başlatır ya da bekleyen sonuçlara dokunur —
  // yanlışlıkla tıklamayı engellemek için komut adının yazılması istenir.
  const needsConfirm = selected.tier > 1;
  const confirmOk = !needsConfirm || confirmText.trim() === selected.kind;
  const argOk = !selected.argKey || argValue.trim().length > 0;
  const canSend = !busy && !live && confirmOk && argOk;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setErr(null);
    try {
      // `ids` (drop_wal) is a comma/space-separated list → a string array the server's validateArgs
      // expects; every other argKey is a single trimmed string.
      const args = !selected.argKey
        ? {}
        : selected.argKey === 'ids'
          ? { ids: argValue.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean) }
          : { [selected.argKey]: argValue.trim() };
      await issueAgentCommand(agentId, selected.kind, args);
      setArgValue('');
      setConfirmText('');
      await load();
    } catch (e) {
      setErr(e instanceof Error && e.message ? 'Komut gönderilemedi — ' + e.message : 'Komut gönderilemedi');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (commandId: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await cancelAgentCommand(agentId, commandId);
      // cancelled:false = tarayıcı komutu çoktan aldı. "İptal edildi" demek YANLIŞ olur:
      // o anda çalışıyor olabilir.
      if (!r.cancelled) setErr('İptal edilemedi — komut temsilcinin tarayıcısına ulaşmış durumda.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'İptal edilemedi');
    } finally {
      setBusy(false);
    }
  };

  const beatAgoMin = lastBeatAt
    ? Math.max(0, Math.round((Date.now() - new Date(lastBeatAt).getTime()) / 60_000))
    : null;

  return (
    <Modal onClose={onClose} maxWidth={560}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <Icon name="support_agent" size={20} color="var(--accent)" />
        <div style={{ fontSize: '16px', fontWeight: 700 }}>Uzaktan destek</div>
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '16px' }}>
        {agentName} · komut, temsilcinin panelinin bir sonraki yoklamasında çalışır (30-60 sn).
        Temsilci ne yapıldığını panelinde görür.
      </div>

      {/* A dark panel can't receive the command — say so BEFORE the supervisor issues one that just
          sits "beklemede" until it expires. Distinct red, louder than the amber pending-command box. */}
      {panelDark && (
        <div
          style={{
            marginBottom: '14px', padding: '10px 12px', borderRadius: '10px',
            background: 'rgba(239,109,109,.1)', border: '1px solid rgba(239,109,109,.4)',
            fontSize: '12.5px', color: '#ef6d6d',
          }}
        >
          <div style={{ fontWeight: 700 }}>
            ⚠ Panel yanıt vermiyor{beatAgoMin != null ? ` (~${beatAgoMin} dk)` : ''}
          </div>
          <div style={{ color: 'var(--dim)', marginTop: '2px' }}>
            Panel donmuş veya kapalı olabilir — gönderdiğiniz komut hemen, belki hiç ULAŞMAYABİLİR ve
            "beklemede" kalabilir. Acilse temsilciyi telefonla arayın.
          </div>
        </div>
      )}

      {live && (
        <div
          style={{
            marginBottom: '14px', padding: '10px 12px', borderRadius: '10px',
            background: 'rgba(224,162,60,.1)', border: '1px solid rgba(224,162,60,.35)',
            fontSize: '12.5px', color: '#e0a23c',
          }}
        >
          <div style={{ fontWeight: 600 }}>Bekleyen komut: {live.kind}</div>
          <div style={{ color: 'var(--dim)', marginTop: '2px' }}>{STATUS_TR[live.status] || live.status}</div>
          {/* Same rule as the drawer and the server: a DEFERRED command stays cancellable until ACCEPTED. */}
          {(!live.delivered_at || (live.deferred === true && !live.accepted_at)) && (
            <button onClick={() => void cancel(live.id)} disabled={busy} style={{ ...ghostBtnStyle, marginTop: '8px' }}>
              İptal et
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '260px', overflow: 'auto', marginBottom: '12px' }}>
        {COMMANDS.map((c) => {
          const on = selected.kind === c.kind;
          return (
            <button
              key={c.kind}
              onClick={() => { setSelected(c); setArgValue(''); setConfirmText(''); }}
              style={{
                all: 'unset', boxSizing: 'border-box', cursor: 'pointer', padding: '8px 10px',
                borderRadius: '8px', border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
                background: on ? 'rgba(96,165,250,.09)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{c.label}</span>
                <span
                  style={{
                    fontSize: '10.5px', padding: '1px 6px', borderRadius: '999px',
                    background: c.tier === 1 ? 'rgba(55,196,107,.14)' : 'rgba(224,162,60,.16)',
                    color: c.tier === 1 ? '#37c46b' : '#e0a23c',
                  }}
                >
                  {TIER_NOTE[c.tier]}
                </span>
              </div>
              <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '2px' }}>{c.hint}</div>
            </button>
          );
        })}
      </div>

      {selected.argKey && (
        <input
          value={argValue}
          onChange={(e) => setArgValue(e.target.value)}
          placeholder={selected.argLabel}
          maxLength={selected.argKey === 'text' ? 280 : selected.argKey === 'ids' ? 1000 : 64}
          style={{ ...inputStyle, marginBottom: '10px' }}
        />
      )}

      {needsConfirm && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginBottom: '5px' }}>
            Onaylamak için komut adını yazın: <b>{selected.kind}</b>
          </div>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} style={inputStyle} />
        </div>
      )}

      {err && <div style={{ fontSize: '12px', color: '#ef6d6d', marginBottom: '10px' }}>{err}</div>}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={onClose} style={ghostBtnStyle}>Kapat</button>
        <button onClick={() => void send()} disabled={!canSend} style={{ ...primaryBtnStyle, opacity: canSend ? 1 : 0.45 }}>
          {busy ? 'Gönderiliyor…' : live ? 'Bekleyen komut var' : 'Gönder'}
        </button>
      </div>

      <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--dim)', marginBottom: '6px' }}>Son komutlar</div>
      {loading && commands.length === 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--dim)' }}>Yükleniyor…</div>
      ) : commands.length === 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--dim)' }}>Bu temsilciye henüz komut gönderilmedi.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflow: 'auto' }}>
          {commands.map((c) => (
            <div key={c.id} style={{ borderTop: '1px solid var(--border)', paddingTop: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' }}>
                <span style={{ fontWeight: 600 }}>{c.kind}</span>
                <span style={{ color: STATUS_COLOR[c.status] || 'var(--dim)' }}>{STATUS_TR[c.status] || c.status}</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--dim)' }}>
                {new Date(c.issued_at).toLocaleString('tr-TR')} · {c.issued_by}
              </div>
              {c.result && Object.keys(c.result).length > 0 && (
                <pre
                  style={{
                    margin: '4px 0 0', padding: '6px 8px', borderRadius: '6px', background: 'var(--surface2, rgba(255,255,255,.04))',
                    fontSize: '10.5px', color: 'var(--dim)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: '120px', overflow: 'auto',
                  }}
                >
                  {JSON.stringify(c.result, null, 1)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
